import { useEffect } from "react";
import type mapboxgl from "mapbox-gl";

// Reused from docs/CWTRC-2268: additive selection styling, never a map fade.
export const ASSIGNED_SOURCE_ID = "rd-assigned-source";
export const ASSIGNED_FILL_ID = "rd-assigned-fill";
export const ASSIGNED_OUTLINE_ID = "rd-assigned-outline";
export const ASSIGNED_REPLICANT_OUTLINE_ID = "rd-assigned-replicant-outline";
export const PLOT_OUTLINE_SOURCE_ID = "rd-plot-outline-source";
export const PLOT_OUTLINE_ID = "rd-plot-outline";
export const AUTO_ASSIGNMENT_DRAFT_SOURCE_ID =
  "rd-auto-assignment-draft-source";
export const AUTO_ASSIGNMENT_DRAFT_FILL_ID = "rd-auto-assignment-draft-fill";
export const AUTO_ASSIGNMENT_DRAFT_OUTLINE_ID =
  "rd-auto-assignment-draft-outline";
export const AUTO_ASSIGNMENT_DRAFT_REPLICANT_OUTLINE_ID =
  "rd-auto-assignment-draft-replicant-outline";
export const DRAFT_SOURCE_ID = "rd-draft-source";
export const DRAFT_FILL_ID = "rd-draft-fill";
export const DRAFT_CASING_ID = "rd-draft-outline-casing";
export const DRAFT_OUTLINE_ID = "rd-draft-outline";
export const DRAFT_REPLICANT_OUTLINE_ID = "rd-draft-replicant-outline";
export const FOCUS_SOURCE_ID = "rd-focused-source";
export const FOCUS_CASING_ID = "rd-focused-outline-casing";
export const FOCUS_OUTLINE_ID = "rd-focused-outline";
export const FOCUS_REPLICANT_CASING_ID = "rd-focused-replicant-casing";
export const FOCUS_REPLICANT_OUTLINE_ID = "rd-focused-replicant-outline";
export const HOVER_SOURCE_ID = "rd-hover-source";
export const HOVER_CASING_ID = "rd-hover-outline-casing";
export const HOVER_OUTLINE_ID = "rd-hover-outline";
export const HOVER_REPLICANT_CASING_ID = "rd-hover-replicant-casing";
export const HOVER_REPLICANT_OUTLINE_ID = "rd-hover-replicant-outline";

const LAYER_STACK = [
  ASSIGNED_FILL_ID,
  PLOT_OUTLINE_ID,
  `${PLOT_OUTLINE_ID}-replicant`,
  ASSIGNED_OUTLINE_ID,
  ASSIGNED_REPLICANT_OUTLINE_ID,
  AUTO_ASSIGNMENT_DRAFT_FILL_ID,
  AUTO_ASSIGNMENT_DRAFT_OUTLINE_ID,
  AUTO_ASSIGNMENT_DRAFT_REPLICANT_OUTLINE_ID,
  FOCUS_CASING_ID,
  FOCUS_REPLICANT_CASING_ID,
  FOCUS_OUTLINE_ID,
  FOCUS_REPLICANT_OUTLINE_ID,
  DRAFT_FILL_ID,
  `${DRAFT_OUTLINE_ID}-non-seed-casing`,
  DRAFT_OUTLINE_ID,
  DRAFT_REPLICANT_OUTLINE_ID,
  `${DRAFT_OUTLINE_ID}-non-seed`,
  `${HOVER_CASING_ID}`,
  `${HOVER_REPLICANT_CASING_ID}`,
  HOVER_OUTLINE_ID,
  HOVER_REPLICANT_OUTLINE_ID,
];

// Enabled Seeds/CP eyes must remain visible over every coloured preview/selection.
// Keep hover above those highlights, without changing other domains' ordering.
const HIGHLIGHT_LAYER_STACK = [
  ASSIGNED_FILL_ID,
  AUTO_ASSIGNMENT_DRAFT_FILL_ID,
  DRAFT_FILL_ID,
  ASSIGNED_OUTLINE_ID,
  ASSIGNED_REPLICANT_OUTLINE_ID,
  AUTO_ASSIGNMENT_DRAFT_OUTLINE_ID,
  AUTO_ASSIGNMENT_DRAFT_REPLICANT_OUTLINE_ID,
  `${DRAFT_OUTLINE_ID}-non-seed-casing`,
  DRAFT_OUTLINE_ID,
  DRAFT_REPLICANT_OUTLINE_ID,
  `${DRAFT_OUTLINE_ID}-non-seed`,
  PLOT_OUTLINE_ID,
  `${PLOT_OUTLINE_ID}-replicant`,
  FOCUS_CASING_ID,
  FOCUS_REPLICANT_CASING_ID,
  FOCUS_OUTLINE_ID,
  FOCUS_REPLICANT_OUTLINE_ID,
  HOVER_CASING_ID,
  HOVER_REPLICANT_CASING_ID,
  HOVER_OUTLINE_ID,
  HOVER_REPLICANT_OUTLINE_ID,
];

const EMPTY_COLLECTION: GeoJSON.FeatureCollection = {
  type: "FeatureCollection",
  features: [],
};

export interface CellFeatureInput {
  cellId: string;
  geometry: GeoJSON.Geometry;
  color?: string;
  outlined?: boolean;
  replicant?: boolean;
  seed?: boolean;
  shadow?: boolean;
}

const toCollection = (
  cells: CellFeatureInput[]
): GeoJSON.FeatureCollection => ({
  type: "FeatureCollection",
  features: cells.map(
    ({ cellId, geometry, color, outlined, replicant, seed, shadow }) => ({
      type: "Feature",
      geometry,
      properties: {
        rdCellId: cellId,
        rdColor: color ?? "#FFFFFF",
        ...(outlined === undefined ? {} : { rdOutlined: outlined }),
        ...(replicant === undefined ? {} : { rdReplicant: replicant }),
        ...(seed === true ? { rdSeed: true } : {}),
        ...(shadow === undefined ? {} : { rdShadow: shadow }),
      },
    })
  ),
});

const setData = (
  map: mapboxgl.Map,
  sourceId: string,
  cells: CellFeatureInput[]
): void => {
  const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined;
  source?.setData(toCollection(cells));
};

const raiseLayers = (
  map: mapboxgl.Map,
  highlightsAboveFills: boolean
): void => {
  (highlightsAboveFills ? HIGHLIGHT_LAYER_STACK : LAYER_STACK).forEach(
    (layerId) => {
      if (map.getLayer(layerId)) {
        map.moveLayer(layerId);
      }
    }
  );
};

const raiseStripNumberLabels = (map: mapboxgl.Map): void => {
  const labelLayers = (map.getStyle()?.layers ?? []).filter(({ id }) =>
    id.endsWith("-strip-area-labels")
  );

  labelLayers.forEach(({ id }) => {
    if (map.getLayer(id)) map.moveLayer(id);
  });
};

export interface UseSelectionLayersParams {
  map: mapboxgl.Map | null;
  assigned: CellFeatureInput[];
  autoAssignmentDraft?: CellFeatureInput[];
  draft: CellFeatureInput[];
  focused?: CellFeatureInput[];
  hovered?: CellFeatureInput[];
  outlines?: CellFeatureInput[];
  keepSeedLayersAboveGrid?: boolean;
  keepHighlightsAboveGrid?: boolean;
}

const useSelectionLayers = ({
  map,
  assigned,
  autoAssignmentDraft = [],
  draft,
  focused = [],
  hovered = [],
  outlines = [],
  keepSeedLayersAboveGrid = false,
  keepHighlightsAboveGrid = false,
}: UseSelectionLayersParams): void => {
  const highlightsAboveFills =
    keepSeedLayersAboveGrid || keepHighlightsAboveGrid;
  useEffect(() => {
    if (!map) return;

    if (!map.getSource(ASSIGNED_SOURCE_ID)) {
      map.addSource(ASSIGNED_SOURCE_ID, {
        type: "geojson",
        data: EMPTY_COLLECTION,
      });
    }
    const addSource = (sourceId: string): void => {
      if (!map.getSource(sourceId)) {
        map.addSource(sourceId, { type: "geojson", data: EMPTY_COLLECTION });
      }
    };
    const addLayer = (layer: mapboxgl.AnyLayer): void => {
      if (!map.getLayer(layer.id)) map.addLayer(layer);
    };
    const nonReplicantFilter = ["!=", ["get", "rdReplicant"], true];
    const replicantFilter = ["==", ["get", "rdReplicant"], true];
    const seedFilter = ["==", ["get", "rdSeed"], true];
    const eyeOutlineWidth: mapboxgl.Expression = [
      "case",
      ["==", ["get", "rdOutlined"], false],
      1,
      4,
    ];
    const outlineWidth: mapboxgl.Expression = [
      "case",
      seedFilter,
      eyeOutlineWidth,
      2,
    ];

    addLayer({
      id: ASSIGNED_FILL_ID,
      type: "fill",
      source: ASSIGNED_SOURCE_ID,
      paint: { "fill-color": ["get", "rdColor"], "fill-opacity": 0.75 },
    });
    addLayer({
      id: ASSIGNED_OUTLINE_ID,
      type: "line",
      source: ASSIGNED_SOURCE_ID,
      filter: ["all", seedFilter, nonReplicantFilter],
      paint: { "line-color": ["get", "rdColor"], "line-width": outlineWidth },
    });
    addLayer({
      id: ASSIGNED_REPLICANT_OUTLINE_ID,
      type: "line",
      source: ASSIGNED_SOURCE_ID,
      filter: ["all", seedFilter, replicantFilter],
      paint: {
        "line-color": ["get", "rdColor"],
        "line-width": outlineWidth,
        "line-dasharray": [1, 1],
      },
    });

    addSource(AUTO_ASSIGNMENT_DRAFT_SOURCE_ID);
    addLayer({
      id: AUTO_ASSIGNMENT_DRAFT_FILL_ID,
      type: "fill",
      source: AUTO_ASSIGNMENT_DRAFT_SOURCE_ID,
      paint: { "fill-color": ["get", "rdColor"], "fill-opacity": 0.32 },
    });
    addLayer({
      id: AUTO_ASSIGNMENT_DRAFT_OUTLINE_ID,
      type: "line",
      source: AUTO_ASSIGNMENT_DRAFT_SOURCE_ID,
      filter: ["!=", ["get", "rdReplicant"], true],
      paint: {
        "line-color": ["get", "rdColor"],
        "line-width": outlineWidth,
        "line-dasharray": [2, 1],
      },
    });
    addLayer({
      id: AUTO_ASSIGNMENT_DRAFT_REPLICANT_OUTLINE_ID,
      type: "line",
      source: AUTO_ASSIGNMENT_DRAFT_SOURCE_ID,
      filter: ["==", ["get", "rdReplicant"], true],
      paint: {
        "line-color": ["get", "rdColor"],
        "line-width": outlineWidth,
        "line-dasharray": [1, 1],
      },
    });

    addSource(DRAFT_SOURCE_ID);
    addSource(PLOT_OUTLINE_SOURCE_ID);
    // Seeds and CP share independent eye boundaries, not assignment styling.
    // Keep the 4px/1px eye rule confined to these white boundary layers.
    addLayer({
      id: PLOT_OUTLINE_ID,
      type: "line",
      source: PLOT_OUTLINE_SOURCE_ID,
      filter: nonReplicantFilter,
      paint: { "line-color": "#FFFFFF", "line-width": eyeOutlineWidth },
    });
    addLayer({
      id: `${PLOT_OUTLINE_ID}-replicant`,
      type: "line",
      source: PLOT_OUTLINE_SOURCE_ID,
      filter: replicantFilter,
      paint: {
        "line-color": "#FFFFFF",
        "line-width": eyeOutlineWidth,
        "line-dasharray": [1, 1],
      },
    });
    addLayer({
      id: DRAFT_FILL_ID,
      type: "fill",
      source: DRAFT_SOURCE_ID,
      paint: {
        "fill-color": ["get", "rdColor"],
        "fill-opacity": ["case", seedFilter, 0.75, 0.45],
      },
    });
    addLayer({
      id: DRAFT_OUTLINE_ID,
      type: "line",
      source: DRAFT_SOURCE_ID,
      filter: ["all", seedFilter, nonReplicantFilter],
      paint: {
        "line-color": ["get", "rdColor"],
        "line-width": outlineWidth,
      },
    });
    addLayer({
      id: DRAFT_REPLICANT_OUTLINE_ID,
      type: "line",
      source: DRAFT_SOURCE_ID,
      filter: ["all", seedFilter, replicantFilter],
      paint: {
        "line-color": ["get", "rdColor"],
        "line-width": outlineWidth,
        "line-dasharray": [1, 1],
      },
    });
    addLayer({
      id: `${DRAFT_OUTLINE_ID}-non-seed`,
      type: "line",
      source: DRAFT_SOURCE_ID,
      filter: ["!=", ["get", "rdSeed"], true],
      paint: { "line-color": "#FFFFFF", "line-width": 2 },
    });
    addLayer({
      id: `${DRAFT_OUTLINE_ID}-non-seed-casing`,
      type: "line",
      source: DRAFT_SOURCE_ID,
      filter: ["!=", ["get", "rdSeed"], true],
      paint: {
        "line-color": "#14151C",
        "line-width": 2,
        "line-opacity": 0.9,
      },
    });

    addSource(FOCUS_SOURCE_ID);
    const addFocusLine = (
      id: string,
      filter: any[],
      color: string,
      width: number | mapboxgl.Expression,
      dashed: boolean
    ): void => {
      addLayer({
        id,
        type: "line",
        source: FOCUS_SOURCE_ID,
        filter,
        paint: {
          "line-color": color,
          "line-width": width,
          ...(color === "#14151C" ? { "line-opacity": 0.9 } : {}),
          ...(dashed ? { "line-dasharray": [1, 1] } : {}),
        },
      });
    };
    const focusCasingWidth: mapboxgl.Expression = [
      "case",
      seedFilter,
      eyeOutlineWidth,
      7,
    ];
    const focusOutlineWidth: mapboxgl.Expression = [
      "case",
      seedFilter,
      ["case", ["==", ["get", "rdOutlined"], false], 1, 2],
      3,
    ];
    addFocusLine(
      FOCUS_CASING_ID,
      nonReplicantFilter,
      "#14151C",
      focusCasingWidth,
      false
    );
    addFocusLine(
      FOCUS_REPLICANT_CASING_ID,
      replicantFilter,
      "#14151C",
      focusCasingWidth,
      true
    );
    addFocusLine(
      FOCUS_OUTLINE_ID,
      nonReplicantFilter,
      "#FFFFFF",
      focusOutlineWidth,
      false
    );
    addFocusLine(
      FOCUS_REPLICANT_OUTLINE_ID,
      replicantFilter,
      "#FFFFFF",
      focusOutlineWidth,
      true
    );

    addSource(HOVER_SOURCE_ID);
    const addHoverLine = (
      id: string,
      filter: any[],
      color: string,
      width: number,
      dashed: boolean
    ): void => {
      const layer: mapboxgl.LineLayer = {
        id,
        type: "line",
        source: HOVER_SOURCE_ID,
        filter,
        paint: {
          "line-color": color,
          "line-width": width,
          ...(dashed ? { "line-dasharray": [1, 1] } : {}),
          ...(color === "#14151C"
            ? {
                "line-opacity": 0.9,
                "line-blur": ["case", ["==", ["get", "rdShadow"], true], 2, 0],
              }
            : {}),
        },
      };
      addLayer(layer);
    };
    addHoverLine(HOVER_CASING_ID, nonReplicantFilter, "#14151C", 7, false);
    addHoverLine(
      HOVER_REPLICANT_CASING_ID,
      replicantFilter,
      "#14151C",
      7,
      true
    );
    addHoverLine(HOVER_OUTLINE_ID, nonReplicantFilter, "#FFFFFF", 3, false);
    addHoverLine(
      HOVER_REPLICANT_OUTLINE_ID,
      replicantFilter,
      "#FFFFFF",
      3,
      true
    );

    return () => {
      // Mapbox can clear its internal style before React runs passive-effect
      // cleanups during map teardown. In that case the style-owned layers and
      // sources are already gone, and getLayer/getSource would throw.
      if (!map.getStyle()) return;

      [...LAYER_STACK].reverse().forEach((layerId) => {
        if (map.getLayer(layerId)) map.removeLayer(layerId);
      });
      [
        FOCUS_SOURCE_ID,
        HOVER_SOURCE_ID,
        PLOT_OUTLINE_SOURCE_ID,
        DRAFT_SOURCE_ID,
        AUTO_ASSIGNMENT_DRAFT_SOURCE_ID,
        ASSIGNED_SOURCE_ID,
      ].forEach((sourceId) => {
        if (map.getSource(sourceId)) map.removeSource(sourceId);
      });
    };
  }, [map]);

  useEffect(() => {
    if (!map) return;
    setData(map, ASSIGNED_SOURCE_ID, assigned);
    setData(map, AUTO_ASSIGNMENT_DRAFT_SOURCE_ID, autoAssignmentDraft);
    setData(map, DRAFT_SOURCE_ID, draft);
    setData(map, FOCUS_SOURCE_ID, focused);
    setData(map, HOVER_SOURCE_ID, hovered);
    setData(map, PLOT_OUTLINE_SOURCE_ID, outlines);
    raiseLayers(map, highlightsAboveFills);
    raiseStripNumberLabels(map);
  }, [
    map,
    assigned,
    autoAssignmentDraft,
    draft,
    focused,
    hovered,
    outlines,
    highlightsAboveFills,
  ]);

  useEffect(() => {
    if (!map || !highlightsAboveFills) return;

    let restoringOrder = false;
    const restoreOrder = (): void => {
      if (restoringOrder) return;
      const layers = map.getStyle()?.layers ?? [];
      const overlayIndex = layers.findIndex(
        ({ id }) => id === ASSIGNED_FILL_ID
      );
      if (
        overlayIndex < 0 ||
        !layers
          .slice(overlayIndex + 1)
          .some(
            (layer) =>
              layer.type === "fill" &&
              typeof layer.source === "string" &&
              /^(?:cloned-)?grid-of-trial-plots-/.test(layer.source)
          )
      ) {
        return;
      }

      // The shared renderer rebuilds opaque cell fills after our data effect.
      // Raise only when those fills overtake Seeds/CP, not on our own style events.
      restoringOrder = true;
      try {
        raiseLayers(map, true);
        raiseStripNumberLabels(map);
      } finally {
        restoringOrder = false;
      }
    };
    map.on("styledata", restoreOrder);
    restoreOrder();
    return () => {
      map.off("styledata", restoreOrder);
    };
  }, [map, highlightsAboveFills]);
};

export default useSelectionLayers;
